import { getIntersectionRoot, getScrollEventTarget } from '@theme/scroll-container';

// Keep chapter links focused and avoid Horizon's history restoration overriding
// the browser's fragment jump after a responsive resize or page restoration.
document.addEventListener('click', (event) => {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const link = event.target.closest?.('.sotp-guide a[href^="#"]');
  if (!link) return;
  const hash = link.getAttribute('href');
  const target = document.getElementById(hash.slice(1));
  if (!target) return;
  event.preventDefault();
  history.pushState(null, '', hash);
  target.focus({ preventScroll: true });
  target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
});

class CraftStory extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.layout = matchMedia('(min-width: 990px)');
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.steps = [...this.querySelectorAll('[data-craft-step]')];
    this.scenes = [...this.querySelectorAll('[data-craft-scene]')];
    this.schedule = () => {
      if (!this.enhanced || this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.updateScene();
      });
    };
    this.connect = () => {
      this.scrollTarget?.removeEventListener('scroll', this.schedule);
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.enhanced = this.layout.matches && !this.motion.matches;
      this.classList.toggle('is-enhanced', this.enhanced);
      if (!this.enhanced) return;
      this.root = getIntersectionRoot();
      this.scrollTarget = getScrollEventTarget();
      this.scrollTarget.addEventListener('scroll', this.schedule, { passive: true });
      this.schedule();
    };
    this.layout.addEventListener('change', this.connect);
    this.motion.addEventListener('change', this.connect);
    window.addEventListener('resize', this.schedule, { passive: true });
    window.addEventListener('pageshow', this.schedule);
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(this.schedule);
      this.steps.forEach((step) => this.resizeObserver.observe(step));
    }
    this.connect();
  }
  updateScene() {
    if (!this.enhanced || !this.isConnected) return;
    const bounds = this.root?.getBoundingClientRect();
    const top = Math.max(0, bounds?.top ?? 0);
    const bottom = Math.min(window.innerHeight, bounds?.bottom ?? window.innerHeight);
    const readingLine = top + (bottom - top) * 0.45;
    let nearest = 0;
    let distance = Infinity;
    // Measure every stage: observer callbacks only include changed intersections,
    // which can leave an old image selected after a large scroll or restoration.
    this.steps.forEach((step, index) => {
      const rect = step.getBoundingClientRect();
      const gap = Math.max(rect.top - readingLine, readingLine - rect.bottom, 0);
      if (gap < distance) {
        nearest = index;
        distance = gap;
      }
    });
    this.activate(nearest);
  }
  activate(index) {
    this.scenes.forEach((scene, i) => scene.classList.toggle('is-active', i === index));
  }
  disconnectedCallback() {
    this.scrollTarget?.removeEventListener('scroll', this.schedule);
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.frame);
    this.frame = null;
    window.removeEventListener('resize', this.schedule);
    window.removeEventListener('pageshow', this.schedule);
    this.layout?.removeEventListener('change', this.connect);
    this.motion?.removeEventListener('change', this.connect);
    this.initialized = false;
  }
}
if (!customElements.get('sotp-craft-story')) customElements.define('sotp-craft-story', CraftStory);
